import { useState, useEffect } from "react";
import { ExpandedContent, Separator, TableContainer, AnexosContainer, ArquivoItem } from "./styles";
import { IItemHistorico } from "../../../../types";
import api from "../../../../../../../services/api";
import { FileText, ExternalLink } from "lucide-react";
import { mapearAnexos } from "../../Compartilhado/utils";
import { Anexo } from "../../Compartilhado/types";
import LogsVazio from "../../Compartilhado/LogsVazio";

interface ExpandedContentProps {
    item: IItemHistorico;
    onNavigateToFile?: (fileId: number) => void;
}

interface CampoAlterado {
    campo: string;
    antes: any;
    depois: any;
}

const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
        'Pendente': 'Pendente',
        'Em Triagem': 'Em Triagem',
        'Em Contratação': 'Em Contratação',
        'Aguardando Execução': 'Aguardando Execução',
        'Em Execução': 'Em Execução',
        'Em Conferência': 'Em Conferência',
        'Finalizada': 'Finalizada',
        'Cancelada': 'Cancelada',
        'Interrompida': 'Interrompida'
    };
    return statusMap[status] || status;
};

const formatarSimNao = (valor: any): string => {
    if (valor === null || valor === undefined || valor === '') return '—';
    if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
    const valorStr = String(valor).toLowerCase();
    if (valorStr === 'sim' || valorStr === 'yes' || valorStr === 'true' || valorStr === '1') return 'Sim';
    if (valorStr === 'nao' || valorStr === 'no' || valorStr === 'false' || valorStr === '0') return 'Não';
    return formatValue(valor);
};

const getFieldLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
        'status': 'Status',
        'atrasada': 'Atrasada',
        'pendente_de_orientacao': 'Pendente de Orientação',
        'status_pendencias': 'Status de Pendências',
        'status_orientacao': 'Status de Orientação'
    };
    return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const compararDados = (dadosAntes: any, dadosDepois: any, dadosExtra?: any): CampoAlterado[] => {
    const campos: CampoAlterado[] = [];

    const nomeAntes = dadosExtra?.nome_profissional_antes || dadosAntes?.nomeProfissional || dadosAntes?.nome_profissional;
    const nomeDepois = dadosExtra?.nome_profissional_depois || dadosDepois?.nomeProfissional || dadosDepois?.nome_profissional;
    if (dadosDepois?.id_usuario !== undefined && dadosAntes?.id_usuario !== dadosDepois.id_usuario) {
        campos.push({
            campo: 'profissional',
            antes: nomeAntes || '—',
            depois: nomeDepois || '—'
        });
    }

    if (dadosDepois?.status && dadosAntes?.status !== dadosDepois.status) {
        campos.push({
            campo: 'status',
            antes: getStatusLabel(dadosAntes?.status || 'Em Execução'),
            depois: getStatusLabel(dadosDepois.status)
        });
    }

    if (dadosDepois?.status_orientacao !== undefined && dadosAntes?.status_orientacao !== dadosDepois.status_orientacao) {
        campos.push({
            campo: 'status_orientacao',
            antes: formatValue(dadosAntes?.status_orientacao),
            depois: formatValue(dadosDepois.status_orientacao)
        });
    }

    if (dadosDepois?.atrasada !== undefined && dadosAntes?.atrasada !== dadosDepois.atrasada) {
        campos.push({
            campo: 'atrasada',
            antes: formatarSimNao(dadosAntes?.atrasada),
            depois: formatarSimNao(dadosDepois.atrasada)
        });
    }

    if (dadosDepois?.pendente_de_orientacao !== undefined && dadosAntes?.pendente_de_orientacao !== dadosDepois.pendente_de_orientacao) {
        campos.push({
            campo: 'pendente_de_orientacao',
            antes: formatarSimNao(dadosAntes?.pendente_de_orientacao),
            depois: formatarSimNao(dadosDepois.pendente_de_orientacao)
        });
    }

    if (dadosDepois?.status_pendencias !== undefined && dadosAntes?.status_pendencias !== dadosDepois.status_pendencias) {
        campos.push({
            campo: 'status_pendencias',
            antes: formatValue(dadosAntes?.status_pendencias),
            depois: formatValue(dadosDepois.status_pendencias)
        });
    }

    return campos;
};

export default function ExpandedContentDemandaInterrompida({ 
    item, 
    onNavigateToFile 
}: ExpandedContentProps) {
    const [camposAlteracoes, setCamposAlteracoes] = useState<CampoAlterado[]>([]);
    const [anexos, setAnexos] = useState<Anexo[]>([]);
    const [motivo, setMotivo] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!item.id) {
            setLoading(false);
            return;
        }

        const buscarDados = async () => {
            try {
                setLoading(true);
                const response = await api.get(`log-historico/${item.id}`);

                if (response.data.status === 'success') {
                    const logs = response.data.result || [];
                    const logProposta = logs.find((log: any) => log.entidade_nome === 'proposta');

                    if (logProposta) {
                        let dadosAntes = logProposta.dados_antes;
                        let dadosDepois = logProposta.dados_depois;
                        let dadosExtra = logProposta.dados_extra;

                        if (typeof dadosAntes === 'string') {
                            try {
                                dadosAntes = JSON.parse(dadosAntes);
                            } catch (e) {
                                dadosAntes = null;
                            }
                        }

                        if (typeof dadosDepois === 'string') {
                            try {
                                dadosDepois = JSON.parse(dadosDepois);
                            } catch (e) {
                                dadosDepois = null;
                            }
                        }

                        if (typeof dadosExtra === 'string') {
                            try {
                                dadosExtra = JSON.parse(dadosExtra);
                            } catch (e) {
                                dadosExtra = null;
                            }
                        }

                        const campos = compararDados(dadosAntes, dadosDepois, dadosExtra);
                        setCamposAlteracoes(campos);

                        if (item.justificativa) {
                            setMotivo(item.justificativa);
                        }
                    } else {
                        setCamposAlteracoes([]);
                        if (item.justificativa) {
                            setMotivo(item.justificativa);
                        }
                    }

                    const anexosMapeados = mapearAnexos(logs);
                    setAnexos(anexosMapeados);
                } else {
                    setCamposAlteracoes([]);
                    setAnexos([]);
                }
            } catch (error: any) {
                setCamposAlteracoes([]);
                setAnexos([]);
            } finally {
                setLoading(false);
            }
        };

        buscarDados();
    }, [item.id, item.justificativa]);

    const handleArquivoClick = (anexo: Anexo) => {
        if (onNavigateToFile && anexo.id) {
            onNavigateToFile(anexo.id);
        }
    };

    if (loading) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    const semDados = camposAlteracoes.length === 0 && anexos.length === 0 && !motivo;

    if (semDados) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    return (
        <ExpandedContent>
            {camposAlteracoes.length > 0 && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Principal</h4>
                        <TableContainer>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th className="field-col">Campo</th>
                                        <th className="before-col">Antes</th>
                                        <th className="after-col">Depois</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {camposAlteracoes.map((campo, index) => (
                                        <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                            <td className="field-cell">{getFieldLabel(campo.campo)}</td>
                                            <td className="before-cell">{formatValue(campo.antes)}</td>
                                            <td className="after-cell">
                                                <div className="after-content">
                                                    <span className="highlight-bar" />
                                                    <span>{formatValue(campo.depois)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </TableContainer>
                    </div>
                </>
            )}
            {motivo && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Motivo da interrupção</h4>
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                            borderRadius: '6px',
                            color: 'black',
                            fontSize: '11px',
                            lineHeight: '20px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            {motivo}
                        </div>
                    </div>
                </>
            )}
            {anexos.length > 0 && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Anexos</h4>
                        <AnexosContainer>
                            {anexos.map((anexo) => (
                                <ArquivoItem 
                                    key={anexo.id} 
                                    onClick={() => handleArquivoClick(anexo)}
                                >
                                    <div className="arquivo-icone">
                                        <FileText size={16} color="#64748B" />
                                    </div>
                                    <div className="arquivo-info">
                                        <div className="arquivo-nome">
                                            {anexo.filename}
                                        </div>
                                        <div className="arquivo-tamanho">
                                            {anexo.size}
                                        </div>
                                    </div>
                                    <div className="arquivo-acao">
                                        <ExternalLink size={14} color="#64748B" />
                                    </div>
                                </ArquivoItem>
                            ))}
                        </AnexosContainer>
                    </div>
                </>
            )}
        </ExpandedContent>
    );
}

